import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { ColorPopoverPicker } from './ColorPopoverPicker';
import { LineThicknessSlider } from './LineThicknessSlider';
import type { ThresholdBundleFormController } from './threshold-bundle-form.hook';

// -- type definitions --

export interface ThresholdStyleStepProps {
  controller: ThresholdBundleFormController;
}

// -- constants --

const DASH_OPTIONS = [
  { value: '', label: 'Continu' },
  { value: '5,5', label: 'Pointillé' },
  { value: '10,5', label: 'Tirets longs' },
  { value: '2,2', label: 'Pointillé fin' },
  { value: '15,5,5,5', label: 'Mixte' },
];

// -- functions --

// -- main component --

export function ThresholdStyleStep({ controller }: ThresholdStyleStepProps) {
  const { formValues } = controller;
  const errors = controller.validationErrors;

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ mb: 0.8 }}>
            Couleur
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <ColorPopoverPicker
              value={formValues.default_style.color}
              onChange={(value) => controller.setStyleField('color', value)}
            />
            <TextField
              required
              value={formValues.default_style.color}
              onChange={(event) =>
                controller.setStyleField('color', event.target.value)
              }
              error={Boolean(errors.style_color)}
              helperText={errors.style_color ?? 'Format rgba(r, g, b, a)'}
              fullWidth
              label="Valeur"
            />
          </Stack>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ mb: 0.8 }}>
            Couleur de remplissage
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <ColorPopoverPicker
              value={formValues.default_style.fill_color}
              onChange={(value) =>
                controller.setStyleField('fill_color', value)
              }
            />
            <TextField
              required
              value={formValues.default_style.fill_color}
              onChange={(event) =>
                controller.setStyleField('fill_color', event.target.value)
              }
              error={Boolean(errors.style_fill_color)}
              helperText={errors.style_fill_color ?? 'Format rgba(r, g, b, a)'}
              fullWidth
              label="Valeur"
            />
          </Stack>
        </Box>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Box sx={{ width: '100%' }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 0.5 }}
          >
            <Typography variant="caption">Épaisseur *</Typography>
            <Typography variant="caption">
              {`${formValues.default_style.width}px`}
            </Typography>
          </Stack>
          <LineThicknessSlider
            value={Number(formValues.default_style.width) || 1}
            min={0.5}
            max={12}
            step={0.5}
            onChange={(value) => controller.setStyleField('width', value)}
          />
          <FormHelperText error={Boolean(errors.style_width)}>
            {errors.style_width ?? 'Supérieur à 0'}
          </FormHelperText>
        </Box>

        <FormControl fullWidth>
          <InputLabel>Style de trait</InputLabel>
          <Select
            value={formValues.default_style.dash}
            label="Style de trait"
            onChange={(event) =>
              controller.setStyleField('dash', event.target.value)
            }
            renderValue={(selected) => {
              const dashValue = (selected as string) ?? '';
              const option =
                DASH_OPTIONS.find((item) => item.value === dashValue) ||
                DASH_OPTIONS[0];
              return (
                <Stack direction="row" alignItems="center" spacing={1.25}>
                  <Box
                    component="svg"
                    viewBox="0 0 50 8"
                    sx={{ width: 46, height: 8, overflow: 'visible' }}
                  >
                    <line
                      x1="0"
                      y1="4"
                      x2="50"
                      y2="4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeDasharray={option.value || undefined}
                    />
                  </Box>
                  <Typography variant="body2">{option.label}</Typography>
                </Stack>
              );
            }}
          >
            {DASH_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Stack direction="row" alignItems="center" spacing={1.25}>
                  <Box
                    component="svg"
                    viewBox="0 0 50 8"
                    sx={{ width: 46, height: 8, overflow: 'visible' }}
                  >
                    <line
                      x1="0"
                      y1="4"
                      x2="50"
                      y2="4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeDasharray={option.value || undefined}
                    />
                  </Box>
                  <Typography variant="body2">{option.label}</Typography>
                </Stack>
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>Optionnel</FormHelperText>
        </FormControl>
      </Stack>
    </Stack>
  );
}
